import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Plus, Wrench, Paperclip } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import apiClient from "@/api/client";
import {
  MANAGED_FILE_MAX_BYTES,
  fileTooLargeMessage,
  findTooLargeFile,
  uploadErrorMessage,
  uploadManagedFile,
} from "@/utils/managedFile";
import { useCreateRequisition } from "../hooks/useRequisitions";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import { getContractorDisplayName } from "@/features/contractors/utils";
import { Button } from "@/components/ui/Button";
import { LineItemFormRow, lineItemSchema } from "../components/LineItemFormRow";
import { autoTriggerLabel } from "../components/AutoLineItemRow";
import { MilestonesEditor, latestMilestoneDate, DEADLINE_BEFORE_MILESTONE_MSG, type MilestoneDraft } from "../components/MilestonesEditor";
import { MinOrderAmountNotice } from "../components/MinOrderAmountNotice";
import { ContractorSuggestionPanel } from "@/features/contractors/components/ContractorSuggestionPanel";
import { sortLeaveLast, withLeaveSuffix, type OnLeaveBrief } from "@/features/contractors/api/leaves";
import { computeMinOrderNotice } from "../utils/minOrderAmount";
import { useAuthStore } from "@/store/auth";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { naturalSortKey } from "@/utils/naturalSort";

// --- Types ---

interface Project { id: string; name: string; short_name: string; is_active: boolean; }
interface ContractorOption { id: string; name: string; short_name: string; is_active: boolean; on_leave?: OnLeaveBrief | null; }
interface LineItemType { id: string; name: string; unit: string; base_rate: string; high_priority_rate?: string | null; attachment_required: boolean; is_active: boolean; auto_trigger_names?: string[]; auto_trigger_name?: string | null; }
interface Agreement { id: string; title: string; min_order_amount?: string | null; line_item_types: LineItemType[]; }

// --- Schema ---

const schema = z.object({
  project: z.string().min(1, "Wybierz przedsięwzięcie"),
  contractor: z.string().optional(),
  description: z
    .string()
    .min(1, "Opis jest wymagany")
    .refine(
      (val) => val.replace(/<[^>]*>/g, "").trim().length >= 10,
      "Opis musi mieć co najmniej 10 znaków"
    ),
  priority: z.enum(["low", "standard", "high"]),
  deadline: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, "Dodaj co najmniej jedną pozycję realizacyjną"),
});

type FormData = z.infer<typeof schema>;

// --- File upload helper ---

const uploadFile = (file: File) => uploadManagedFile(file);

// --- Queries ---

function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: () => apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });
}

function useContractors() {
  return useQuery({
    queryKey: ["contractors", "active"],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorOption[] }>("/contractors/", { params: { is_active: "true" } })
        .then((r) => r.data.data),
  });
}

function useContractorLineItems(contractorId: string | undefined) {
  return useQuery({
    queryKey: ["contractor-agreements", contractorId],
    queryFn: () =>
      apiClient
        .get<{ data: Agreement[] }>(`/contractors/${contractorId}/agreements/`)
        .then((r) => r.data.data),
    enabled: !!contractorId,
  });
}

// --- Component ---

export function RequisitionFormPage() {
  const navigate = useNavigate();
  const createMutation = useCreateRequisition();
  const user = useAuthStore((s) => s.user);
  const canAddCustomItems =
    user?.role === "director" || user?.role === "coordinator";

  const [attachments, setAttachments] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { register, control, handleSubmit, setValue, formState: { errors } } =
    useForm<FormData>({
      resolver: zodResolver(schema),
      defaultValues: {
        priority: "standard",
        line_items: [
          { is_custom: false, line_item_type: "", quantity: 1, description: "", attachment_required: true, required_file_extension: "", result_files_expected: false },
        ],
      },
    });

  const { fields, append, remove } = useFieldArray({ control, name: "line_items" });
  const [milestones, setMilestones] = useState<MilestoneDraft[]>([]);
  const contractorId = useWatch({ control, name: "contractor" });
  const watchedProject = useWatch({ control, name: "project" });
  const watchedDeadline = useWatch({ control, name: "deadline" });
  const watchedLineItems = useWatch({ control, name: "line_items" });
  const watchedPriority = useWatch({ control, name: "priority" });
  const msLatest = latestMilestoneDate(milestones);
  const deadlineTooEarly = !!watchedDeadline && !!msLatest && watchedDeadline < msLatest;

  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: contractors, isLoading: contractorsLoading } = useContractors();
  const { data: agreements } = useContractorLineItems(contractorId || undefined);

  // „Kwota minimalna zamówienia" z umowy ramowej proponowanego podwykonawcy — liczona
  // na żywo z wpisywanych pozycji. Informacja dla składającego, nie blokada.
  const minOrderNotice = useMemo(
    () => (contractorId ? computeMinOrderNotice(agreements, watchedLineItems, watchedPriority) : null),
    [contractorId, agreements, watchedLineItems, watchedPriority],
  );

  // Pozycje wycofane z umowy ramowej nie wchodzą na nowe zapotrzebowanie — nie ma ich
  // nawet na liście wyboru (backend i tak odrzuci taki zapis).
  const lineItemOptions = useMemo<{ value: string; label: string }[]>(() => {
    if (!agreements) return [];
    return agreements.flatMap((a) =>
      a.line_item_types.filter((lit) => lit.is_active !== false).map((lit) => ({
        value: lit.id,
        label: `${lit.name} — ${lit.unit}${lit.base_rate != null ? ` @ ${parseFloat(lit.base_rate).toLocaleString("pl-PL", { useGrouping: "always" })} zł` : ""}${lit.auto_trigger_names?.length || lit.auto_trigger_name ? ` (dodawana automatycznie przy ${autoTriggerLabel(lit)})` : ""}`,
      }))
    );
  }, [agreements]);

  const lineItemAttachmentMap = useMemo<Record<string, boolean>>(() => {
    if (!agreements) return {};
    return Object.fromEntries(
      agreements.flatMap((a) =>
        a.line_item_types
          .filter((lit) => lit.is_active !== false)
          .map((lit) => [lit.id, lit.attachment_required]),
      )
    );
  }, [agreements]);

  useEffect(() => {
    fields.forEach((_, index) => {
      setValue(`line_items.${index}.line_item_type`, "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorId]);

  const projectOptions = useMemo(
    () =>
      (projects ?? [])
        .filter((p) => p.is_active)
        .map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` }))
        .sort((a, b) => naturalSortKey(a.label).localeCompare(naturalSortKey(b.label))),
    [projects]
  );

  // Firma na urlopie w oknie składania zamówienia (dziś + 7 dni) idzie na dół pickera
  // z dopiskiem „(urlop od–do)" — nadal do wyboru.
  const contractorOptions = useMemo(
    () =>
      sortLeaveLast(
        (contractors ?? []).map((c) => {
          const display = getContractorDisplayName(c);
          const label = c.short_name ? `${display} (${c.short_name})` : display;
          return { value: c.id, label: withLeaveSuffix(label, c.on_leave), onLeave: !!c.on_leave };
        }),
      ),
    [contractors]
  );

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadFile(file);
    return result.url ?? "";
  }, []);

  const onSubmit = async (data: FormData) => {
    if (deadlineTooEarly) return;
    let fileIds: string[] = [];
    setUploadError(null);
    if (attachments.length > 0) {
      setIsUploading(true);
      try {
        const uploaded = await Promise.all(attachments.map(uploadFile));
        fileIds = uploaded.map((f) => f.id);
      } catch (err) {
        // Without this the rejection escaped the submit handler: the button went back
        // to „Zapisz jako szkic" and nothing said why (2026-10-20).
        setUploadError(uploadErrorMessage(err, "Nie udało się wgrać załączników — spróbuj ponownie."));
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const line_items = data.line_items.map((item) => {
      if (item.is_custom) {
        return {
          line_item_type: null,
          custom_name: item.custom_name,
          custom_unit: item.custom_unit,
          custom_unit_rate: item.custom_unit_rate,
          quantity: item.quantity,
          description: item.description,
          attachment_required: item.attachment_required,
          required_file_extension: item.required_file_extension,
          result_files_expected: item.result_files_expected,
        };
      }
      return {
        line_item_type: item.line_item_type,
        quantity: item.quantity,
        description: item.description,
        attachment_required: item.attachment_required,
        required_file_extension: item.required_file_extension,
        result_files_expected: item.result_files_expected,
      };
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const milestonesPayload = milestones
      .filter((m) => m.label.trim() && m.due_date)
      .map((m) => ({ label: m.label.trim(), due_date: m.due_date }));
    createMutation.mutate(
      { ...data, deadline: data.deadline || null, line_items, milestones: milestonesPayload, files: fileIds } as any,
      // `created` w stanie nawigacji → lista pokaże czerwony banner „sprawdź i zaakceptuj".
      { onSuccess: () => navigate("/requisitions", { state: { created: true } }) },
    );
  };

  if (projectsLoading || contractorsLoading) return <PageLoader />;

  const isPending = createMutation.isPending || isUploading;

  return (
    <div className="max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Nowe zapotrzebowanie</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">

        <SearchableSelect
          label="Przedsięwzięcie"
          placeholder={projectsLoading ? "Ładowanie..." : "— wybierz przedsięwzięcie —"}
          options={projectOptions}
          error={errors.project?.message}
          disabled={projectsLoading}
          {...register("project")}
        />

        {/* Controller (not register): SearchableSelect only reflects values passed via the
            value prop, so a pick from the suggestion panel (setValue) needs a controlled field */}
        <Controller
          name="contractor"
          control={control}
          render={({ field }) => (
            <SearchableSelect
              label="Podwykonawca (opcjonalnie)"
              placeholder="— brak / do ustalenia —"
              options={contractorOptions}
              disabled={contractorsLoading}
              name={field.name}
              value={field.value ?? ""}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
        {/* Podpowiedź podwykonawcy dla wybranego przedsięwzięcia: historia zleceń (także na
            tym przedsięwzięciu), oceny, „Zasady podpowiadania" + AI. Propozycja, nie wymóg. */}
        {!!watchedProject && (
          <ContractorSuggestionPanel
            scope="standard"
            projectId={watchedProject}
            selectedId={contractorId || undefined}
            onPick={(id) => setValue("contractor", id, { shouldDirty: true })}
            deadline={watchedDeadline || undefined}
          />
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Opis zapotrzebowania</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value ?? ""}
                onChange={field.onChange}
                placeholder="Opisz szczegółowo zakres prac, wymagania lub inne istotne informacje…"
                onImageUpload={handleImageUpload}
              />
            )}
          />
          {errors.description && (
            <p className="text-xs text-red-600">{errors.description.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
            <Paperclip size={14} />
            Załączniki
          </label>
          <FileDropZone
            files={attachments}
            onFilesChange={(files) => {
              const tooLarge = findTooLargeFile(files);
              setUploadError(tooLarge ? fileTooLargeMessage(tooLarge) : null);
              setAttachments(files.filter((f) => f.size <= MANAGED_FILE_MAX_BYTES));
            }}
            multiple
          />
        </div>

        <Select
          label="Priorytet"
          options={[
            { value: "low", label: "Niski" },
            { value: "standard", label: "Standardowy" },
            { value: "high", label: "Wysoki" },
          ]}
          error={errors.priority?.message}
          {...register("priority")}
        />

        <div>
          <div className="flex flex-col gap-1">
            <label htmlFor="req-form-deadline" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">Termin realizacji (opcjonalny)</label>
            <Controller
              name="deadline"
              control={control}
              render={({ field }) => (
                <DatePicker id="req-form-deadline" value={field.value ?? ""} onChange={field.onChange} />
              )}
            />
          </div>
          {deadlineTooEarly && (
            <p className="mt-1 text-xs text-red-600">
              {DEADLINE_BEFORE_MILESTONE_MSG} ({msLatest}).
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Pozycje realizacyjne</h3>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() =>
                  append({ is_custom: false, line_item_type: "", quantity: 1, description: "", attachment_required: true, required_file_extension: "", result_files_expected: false })
                }
              >
                <Plus size={14} />
                Z umowy
              </Button>
              {canAddCustomItems && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    append({ is_custom: true, custom_name: "", custom_unit: "", custom_unit_rate: undefined, quantity: 1, description: "", attachment_required: false, required_file_extension: "", result_files_expected: false })
                  }
                  className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                >
                  <Wrench size={14} />
                  Niestandardowa
                </Button>
              )}
            </div>
          </div>
          {errors.line_items?.root && (
            <p className="text-xs text-red-600 mb-2">{errors.line_items.root.message}</p>
          )}

          {!contractorId && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
              Wybierz podwykonawcę, aby móc wskazać pozycje z umowy ramowej. Możesz też dodać pozycje niestandardowe bez wyboru podwykonawcy.
            </p>
          )}

          <div className="flex flex-col gap-3">
            {fields.map((field, index) => (
              <LineItemFormRow
                key={field.id}
                index={index}
                control={control}
                register={register}
                errors={errors}
                lineItemOptions={lineItemOptions}
                contractorId={contractorId || undefined}
                onRemove={() => remove(index)}
                lineItemAttachmentMap={lineItemAttachmentMap}
                setValue={setValue}
              />
            ))}
          </div>
          <MinOrderAmountNotice notice={minOrderNotice} className="mt-3" />
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <MilestonesEditor value={milestones} onChange={setMilestones} />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Opcjonalne. Przeniosą się na zamówienie przy zatwierdzaniu — zatwierdzający może je jeszcze zmienić.
          </p>
        </div>

        {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
        {createMutation.isError && (
          <p className="text-sm text-red-600">
            {uploadErrorMessage(createMutation.error, "Nie udało się zapisać zapotrzebowania — spróbuj ponownie.")}
          </p>
        )}
        <div className="flex gap-3 pt-2">
          <Button type="submit" loading={isPending} disabled={deadlineTooEarly}>
            {isUploading ? "Przesyłanie plików…" : "Zapisz jako szkic"}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate("/requisitions")}>
            Anuluj
          </Button>
        </div>
      </form>
    </div>
  );
}
